/**
 * @fileoverview Tooltip - Accessible hover tooltip components
 *
 * Purpose: Provides shadcn/ui tooltips with full Radix UI accessibility
 * Dependencies: Radix UI Tooltip primitives
 * Used by: Icon buttons, truncated text, help indicators throughout the app
 *
 * Components exported:
 * - TooltipProvider: Root provider (wrap your app or component tree)
 * - Tooltip: Root container for a single tooltip
 * - TooltipTrigger: Element that triggers the tooltip on hover/focus
 * - TooltipContent: The tooltip content bubble
 *
 * Features:
 * - Appears on hover and focus
 * - Dismisses on ESC key
 * - Automatic positioning (avoids viewport edges)
 * - Smooth fade-in/out animations
 * - Accessible (ARIA labels, keyboard support)
 * - Configurable delay
 *
 * @example
 * // Wrap your app with TooltipProvider
 * <TooltipProvider>
 *   <Tooltip>
 *     <TooltipTrigger asChild>
 *       <Button variant="ghost" size="icon">
 *         <HelpCircle className="h-4 w-4" />
 *       </Button>
 *     </TooltipTrigger>
 *     <TooltipContent>
 *       <p>Click for more information</p>
 *     </TooltipContent>
 *   </Tooltip>
 * </TooltipProvider>
 */

"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/utils/misc";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * TooltipContent displays the tooltip bubble
 *
 * Features:
 * - Automatic positioning to avoid viewport edges
 * - Smooth fade and slide animations
 * - Compact, non-intrusive design
 * - Dark mode support
 * - Shadow and border for visibility
 * - Configurable side offset
 *
 * @component
 * @example
 * <TooltipContent>
 *   <p>Helpful explanation text</p>
 * </TooltipContent>
 *
 * // With custom side offset
 * <TooltipContent sideOffset={10}>
 *   <p>More spacing from trigger</p>
 * </TooltipContent>
 */
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 origin-[--radix-tooltip-content-transform-origin] overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
